// 2026-07-25:GoogleAnalyticsSettingsCard — Settings ページ内の GA4 OAuth カード。
//
// UX 仕様(2026-07-25 修正):
// - backend に OAuth 未設定 (env 欠如) → Card 内に「管理者が Google OAuth を設定してください」表示、
//   「Google で接続」ボタン disabled。alert / popup 一切 出さない。
// - OAuth 設定済み + 未連携 → 「Google で接続」ボタン enabled。click で 即 Google 跳转。
//   中间 alert 一切 出さない。失敗時 のみ inline エラー 表示。
// - callback 後の Property 選択 UI → Card 内 に inline 表示。「保存」後 状態 自然 に 変わる。
//   alert / popup 一切 出さない。
// - 連携解除 → Card 内 に inline confirm UI を 表示、 「解除」押下 で 実行。
//   window.confirm / window.alert 一切 使用 しない。
//
// OAuth 未設定 の 検知 は /api/ga4/status を 起動時 に 1 回 取得。
//
// URL query 区分 (2026-07-25):
// - ?ga4_oauth=success&ga4_select=1&pt=xxx  → backend callback 成功,Property 選択 UI 表示
// - ?ga4_error=<safe_code>                  → backend callback 失敗,その error を 表示
//                                              (401/403 等の app session error とは 分離)
//
// App session 失効(別 経路):
// - apiFetch が 401 → 自動 refresh 試行 → 失敗 → AuthProvider が user=null →
//   ProtectedRoute が /login に redirect → Settings ページ 自体 表示されない。
//   したがって Settings 内 では「session 失効」を 扱う 必要 なし。

import { useCallback, useEffect, useState } from "react";
import {
  Save, Trash2, RefreshCw, AlertCircle, CheckCircle2, ExternalLink, Info,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { ApiError } from "../../api/client";
import {
  ga4Api,
  type Ga4Connection,
  type Ga4PropertySummary,
} from "../../api/ga4";

type SelectState =
  | { kind: "none" }
  | { kind: "loading" }
  | { kind: "ready"; pendingToken: string; googleEmail: string; properties: Ga4PropertySummary[] }
  | { kind: "saving" }
  | { kind: "error"; message: string };

type AdminHint =
  | { kind: "hidden" }
  | { kind: "shown"; message: string };

/** callback 側 から 返る safe error code → 日文 message */
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Google 認証がキャンセルされました。",
  missing_params: "OAuth パラメータが不足しています。",
  AUTH_GA4_INVALID_STATE: "OAuth state が不正です。もう一度お試しください。",
  AUTH_GA4_STATE_EXPIRED: "OAuth state が期限切れです。もう一度お試しください。",
  AUTH_GA4_TOKEN_FAILED: "Google トークン取得に失敗しました。",
  AUTH_GA4_PROPERTIES_FAILED: "GA4 Property 一覧取得に失敗しました。",
  AUTH_GA4_USERINFO_FAILED: "Google ユーザー情報取得に失敗しました。",
  AUTH_GA4_STATE_NO_USER: "OAuth state に user 情報がありません。もう一度お試しください。",
  forbidden: "システム管理者のみ OAuth 連携できます。",
  user_inactive_or_missing: "ユーザーアカウントが無効です。",
};

function oauthErrorMessage(code: string): string {
  return OAUTH_ERROR_MESSAGES[code] ?? `OAuth エラー: ${code}`;
}

export function GoogleAnalyticsSettingsCardInner() {
  const [oauthConfigured, setOauthConfigured] = useState<boolean | null>(null);
  const [conn, setConn] = useState<Ga4Connection | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [pendingLoadError, setPendingLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"load" | "delete" | "auth" | null>(null);
  const [selectState, setSelectState] = useState<SelectState>({ kind: "none" });
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [selectedPropId, setSelectedPropId] = useState<string>("");

  const load = useCallback(async () => {
    setBusy("load");
    setLoadError(null);
    try {
      const data = await ga4Api.getConnection();
      setConn(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setConn(null);
      } else if (e instanceof ApiError) {
        setLoadError(e.message);
        setConn(null);
      } else {
        setLoadError(String(e));
        setConn(null);
      }
    } finally {
      setLoaded(true);
      setBusy(null);
    }
  }, []);

  // 起動:OAuth 設定状態を 1 回 取得 + 接続情報 取得 + callback URL パラメータ処理
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const status = await ga4Api.getStatus();
        if (alive) setOauthConfigured(status.configured);
      } catch {
        if (alive) setOauthConfigured(false);
      }
    })();
    void load();

    const url = new URL(window.location.href);
    const sel = url.searchParams.get("ga4_select");
    const pt = url.searchParams.get("pt");
    const err = url.searchParams.get("ga4_error");
    if (err) {
      // OAuth callback 段階 の error。authError として 表示。
      setAuthError(oauthErrorMessage(err));
      // URL cleanup
      url.searchParams.delete("ga4_error");
      window.history.replaceState({}, "", url.toString());
    } else if (sel === "1" && pt) {
      void loadPending(pt, url);
    }
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadPending(pt: string, urlObj: URL) {
    setSelectState({ kind: "loading" });
    setPendingLoadError(null);
    try {
      const res = await fetch(`/api/ga4/auth/pending?pt=${encodeURIComponent(pt)}`, {
        credentials: "include",
      });
      const j = await res.json();
      if (!res.ok || j.success === false) {
        // pending 読み込み 段階 の error。pendingLoadError として 表示(callback の OAuth error とは 分離)。
        const code = j.error?.code ?? `HTTP ${res.status}`;
        const msg = j.error?.message ?? `HTTP ${res.status}`;
        setPendingLoadError(`Property 候補 読み込み失敗 (${code}): ${msg}`);
        setSelectState({ kind: "none" });
        return;
      }
      const data = j.data as { google_email: string; properties: Ga4PropertySummary[] };
      setSelectState({
        kind: "ready",
        pendingToken: pt,
        googleEmail: data.google_email,
        properties: data.properties,
      });
      if (data.properties[0]) setSelectedPropId(data.properties[0].property_id);
    } catch (e) {
      setPendingLoadError(
        `Property 候補 読み込み失敗: ${e instanceof Error ? e.message : String(e)}`,
      );
      setSelectState({ kind: "none" });
    } finally {
      urlObj.searchParams.delete("ga4_select");
      urlObj.searchParams.delete("pt");
      urlObj.searchParams.delete("n");
      window.history.replaceState({}, "", urlObj.toString());
    }
  }

  // 「Google で接続」ボタン:alert 一切 使わない。失敗時 のみ inline 表示。
  async function handleConnect() {
    if (!oauthConfigured) return;
    setBusy("auth");
    setAuthError(null);
    try {
      const { url } = await ga4Api.getAuthUrl();
      // 成功 → 即 Google 跳转(window.location.href で 全ページ遷移)
      window.location.href = url;
      // 以降 この component は unmount される(button busy の 解除 不要)
    } catch (e) {
      // backend env 抜け / network 失敗 → inline のみ、alert 不可
      const msg = e instanceof ApiError ? e.message : String(e);
      setAuthError(msg);
      setBusy(null);
    }
  }

  async function handleSaveProperty() {
    if (selectState.kind !== "ready") return;
    const prop = selectState.properties.find((p) => p.property_id === selectedPropId);
    setSelectState({ kind: "saving" });
    try {
      const saved = await ga4Api.selectProperty({
        pending_token: selectState.pendingToken,
        property_id: selectedPropId,
        property_name: prop?.property_name ?? null,
      });
      setConn(saved);
      setSelectState({ kind: "none" });
      setSelectedPropId("");
      // alert 一切 出さない。UI 状態 が 「連携中」 に 自然に 変わる。
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setSelectState({ kind: "error", message: msg });
    }
  }

  // 連携解除:window.confirm の 代わりに inline confirm UI。
  async function handleConfirmDisconnect() {
    setBusy("delete");
    setConfirmDisconnect(false);
    try {
      await ga4Api.deleteConnection();
      try {
        const storage = window.sessionStorage;
        for (const key of Object.keys(storage)) {
          if (key.startsWith("analytics.report.") || key.startsWith("analytics.aiInsight.") ||
              key.startsWith("mobile.analytics.aiInsight.")) {
            storage.removeItem(key);
          }
        }
      } catch {
        // Storage may be unavailable; the GA4 pages do not read these old caches.
      }
      setConn(null);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setLoadError(msg);
    } finally {
      setBusy(null);
    }
  }

  // 表示 helper
  const isAdminNotConfigured = oauthConfigured === false;

  return (
    <>
      {/* (1) backend env 未設定 → 管理者向け案内 + ボタン disabled */}
      {isAdminNotConfigured && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: 10,
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <Info size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
            管理者が Google OAuth を設定してください。
            <div style={{ fontSize: 11, color: "#a16207", marginTop: 4 }}>
              設定は backend/.env.local の GA4_OAUTH_CLIENT_ID / GA4_OAUTH_CLIENT_SECRET
              / GA4_OAUTH_REDIRECT_URI で行います。
            </div>
          </div>
        </div>
      )}

      {/* (2) OAuth callback 段階 の error(URL ?ga4_error=...)。最優先 表示。 */}
      {authError && (
        <div
          style={{
            padding: "10px 12px",
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
            display: "flex",
            gap: 6,
            alignItems: "flex-start",
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Google OAuth 連携に失敗しました</div>
            <div style={{ fontSize: 12, color: "#7f1d1d" }}>{authError}</div>
          </div>
        </div>
      )}

      {/* (3) pending 読み込み 段階 の error(callback は 成功 したが Property 一覧 が 取れ ない)。
            app session 失効 とは 分離(callback 直後 = app session 有効 の はず)。 */}
      {pendingLoadError && (
        <div
          style={{
            padding: "10px 12px",
            background: "#fffbeb",
            color: "#92400e",
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
            display: "flex",
            gap: 6,
            alignItems: "flex-start",
            border: "1px solid #fde68a",
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Property 候補 の 読み込み に 失敗</div>
            <div style={{ fontSize: 12, color: "#92400e" }}>{pendingLoadError}</div>
            <div style={{ fontSize: 11, color: "#a16207", marginTop: 4 }}>
              OAuth 連携は 完了 している 可能性 が あります。再度 「Google で 接続」 を 実行 してください。
            </div>
          </div>
        </div>
      )}

      {/* (4) loadError(getConnection) / selectState.error(saveProperty) の 一般エラー */}
      {(loadError || (selectState.kind === "error" ? selectState.message : null)) && (
        <div
          style={{
            padding: "10px 12px",
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
            display: "flex",
            gap: 6,
            alignItems: "flex-start",
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{loadError || (selectState.kind === "error" ? selectState.message : "")}</div>
        </div>
      )}

      {/* (3) 未連携 + OAuth 設定済 + 選択 UI 出てない → 案内文 */}
      {!conn && selectState.kind !== "ready" && selectState.kind !== "loading" && oauthConfigured === true && (
        <div
          style={{
            padding: "16px",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
              Google Analytics 4 と連携すると、/analytics と /dashboard の数値がリアルタイムで取得できます。
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
              スコープは analytics.readonly のみ申請します。書き込み権限は付与されません。
            </div>
          </div>
        </div>
      )}

      {/* (4) 連携中:接続情報 */}
      {conn && (
        <div
          style={{
            padding: "12px 16px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div className="row-between">
            <span style={{ fontSize: 12, color: "#6b7280" }}>Google Account</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              {conn.google_email}
            </span>
          </div>
          <div className="row-between">
            <span style={{ fontSize: 12, color: "#6b7280" }}>Property</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              {conn.property_name ?? conn.property_id}
              {conn.property_name ? ` (${conn.property_id})` : ""}
            </span>
          </div>
          <div className="row-between">
            <span style={{ fontSize: 12, color: "#6b7280" }}>連携状態</span>
            <Badge color="green" dot>
              <CheckCircle2 size={11} /> 接続中
            </Badge>
          </div>
        </div>
      )}

      {/* (5) Property 選択 UI(callback 後) */}
      {selectState.kind === "ready" && (
        <div
          style={{
            padding: "12px 16px",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 10,
            marginTop: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1d4ed8", marginBottom: 4 }}>
            Property を選択
          </div>
          <div style={{ fontSize: 12, color: "#374151", marginBottom: 10 }}>
            Google Account: {selectState.googleEmail}
          </div>
          {selectState.properties.length === 0 ? (
            <div style={{ fontSize: 12, color: "#b91c1c" }}>
              このアカウントでアクセスできる GA4 Property がありません。
            </div>
          ) : (
            <select
              className="select"
              style={{ width: "100%", height: 36 }}
              value={selectedPropId}
              onChange={(e) => setSelectedPropId(e.target.value)}
            >
              {selectState.properties.map((p) => (
                <option key={p.property_id} value={p.property_id}>
                  {p.property_name || p.property_id} ({p.account})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {selectState.kind === "loading" && (
        <div style={{ padding: "12px", fontSize: 12, color: "#6b7280" }}>
          プロパティ一覧を取得中...
        </div>
      )}

      {/* (6) 連携解除 inline confirm(「連携解除」押下後 表示) */}
      {confirmDisconnect && conn && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 10,
            marginTop: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#7f1d1d", marginBottom: 10 }}>
            Google Analytics 連携を解除しますか? 関連データは削除されますが、再連携は可能です。
          </div>
          <div className="row gap-8">
            <Button variant="secondary" onClick={() => setConfirmDisconnect(false)} disabled={busy !== null}>
              キャンセル
            </Button>
            <Button
              iconLeft={<Trash2 size={13} />}
              onClick={handleConfirmDisconnect}
              disabled={busy !== null}
            >
              連携を解除
            </Button>
          </div>
        </div>
      )}

      <hr className="divider" />

      <div className="row gap-8">
        {/* 「Google で接続」:未連携 かつ OAuth 設定済 かつ Property 選択 UI 出てない とき 表示。
           OAuth 未設定 / Property 選択中 → ボタン 自体 表示しない(disabled にもしない)。 */}
        {!conn && selectState.kind !== "ready" && selectState.kind !== "loading" && oauthConfigured === true && (
          <Button onClick={handleConnect} disabled={busy !== null || !loaded}>
            Google で接続
          </Button>
        )}
        {/* OAuth 未設定 时 は 「管理者が Google OAuth を設定してください」案内 文 だけ。
           ボタン 自体 出さない(押せない ボタン を 見せても 混乱 するため)。 */}

        {/* 連携中:再読み込み + 連携解除 */}
        {conn && !confirmDisconnect && (
          <>
            <Button
              variant="ghost"
              iconLeft={<RefreshCw size={13} />}
              onClick={load}
              disabled={busy !== null}
            >
              再読み込み
            </Button>
            <Button
              variant="ghost"
              iconLeft={<Trash2 size={13} />}
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy !== null}
            >
              連携解除
            </Button>
          </>
        )}

        {/* Property 選択後:保存(alert 一切 出さない、自然 に 状態 変わる) */}
        {selectState.kind === "ready" && (
          <Button
            iconLeft={<Save size={13} />}
            onClick={handleSaveProperty}
            disabled={!selectedPropId || selectState.properties.length === 0}
          >
            保存
          </Button>
        )}
      </div>

      {oauthConfigured === true && !conn && selectState.kind !== "ready" && (
        <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280" }}>
          <ExternalLink size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
          「Google で接続」を押すと Google のログイン画面へ移動します。
        </div>
      )}
    </>
  );
}
